import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { API, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { PageSpinner } from "@/components/Spinner";

export default function ResetPasswordPage() {
  const { token } = useParams();
  const nav = useNavigate();
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/auth/reset-password/${token}`)
      .then((r) => setMeta(r.data))
      .catch((e) => setError(formatApiError(e)));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) return toast.error("Password must be at least 6 characters");
    if (pw !== pw2) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: pw });
      toast.success("Password updated. Please sign in.");
      nav("/login", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen grid place-items-center bg-bone p-8">
        <div className="max-w-md text-center">
          <div className="eyebrow">Reset password</div>
          <h1 className="text-2xl mt-2">Link not valid</h1>
          <p className="text-sm text-ink-2 mt-2">{error}</p>
          <p className="text-xs text-ink-2 mt-4">Ask your admin to generate a fresh link.</p>
        </div>
      </div>
    );
  }
  if (!meta) return <PageSpinner />;

  return (
    <div className="min-h-screen grid place-items-center bg-bone p-8">
      <form onSubmit={submit} className="w-full max-w-sm" data-testid="reset-form">
        <div className="flex items-center gap-2 mb-6">
          <img src="/brand/aisensy-dark.svg" alt="AiSensy" className="h-6 w-auto" />
        </div>
        <div className="eyebrow">Reset password</div>
        <h1 className="text-2xl mt-1">Hi {meta.name?.split(" ")[0]},</h1>
        <p className="text-sm text-ink-2 mt-1">
          Set a new password for <span className="font-semibold">{meta.email}</span>.
        </p>

        <div className="space-y-4 mt-6">
          <div>
            <Label className="eyebrow">New password</Label>
            <Input
              type="password"
              className="mt-1"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              data-testid="reset-pw"
              required
              minLength={6}
            />
          </div>
          <div>
            <Label className="eyebrow">Confirm password</Label>
            <Input
              type="password"
              className="mt-1"
              value={pw2}
              onChange={(e) => setPw2(e.target.value)}
              data-testid="reset-pw2"
              required
              minLength={6}
            />
          </div>
        </div>

        <Button
          type="submit"
          disabled={busy}
          data-testid="reset-submit"
          className="w-full mt-6 rounded-md bg-[color:var(--aisensy-dark)] hover:bg-[color:var(--aisensy-dark-2)] text-white h-11"
        >
          {busy ? "Updating…" : "Update password"}
        </Button>
      </form>
    </div>
  );
}
